import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, FileText, Package, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { Sunflower, SunflowerMini } from "@/components/site/Sunflower";
import { LOGO_URL } from "@/lib/assets";
import { getPricing, formatUSD } from "@/lib/pricing";

const categoryIcon = {
  Ebook: BookOpen,
  "Children's Story": BookOpen,
  "Parent Guidebook": FileText,
  "Travel Guide": Package,
  "Printable Worksheets": FileText,
  "Curriculum Pack": Package,
};

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/products");
        setProducts(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const categories = ["All", ...Array.from(new Set(products.map((p) => p.category)))];
  const shown =
    filter === "All" ? products : products.filter((p) => p.category === filter);

  return (
    <div data-testid="shop-page">
      <section className="section-pad relative overflow-hidden petal-bg">
        <div className="absolute -top-16 -right-16 opacity-35 pointer-events-none hidden md:block">
          <Sunflower size={260} className="animate-slowspin" />
        </div>
        <div className="relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8 max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-brand-leafDeep bg-brand-honey/50 border border-brand-sun/40 rounded-full px-4 py-1.5 mb-6 w-fit">
              <SunflowerMini size={18} />
              Shop
            </span>
            <h1 className="font-heading font-normal text-[2.75rem] sm:text-5xl lg:text-[4.25rem] leading-[1.02] tracking-tight">
              Printable tools for
              <br />
              <span className="sunray-underline">calmer homes</span> and classrooms.
            </h1>
            <p className="mt-7 text-brand-muted text-base md:text-lg leading-relaxed font-body">
              Every resource here was made in Nada's real sessions — with real
              kids. Instant PDF delivery after checkout.
            </p>
          </div>
          <div className="lg:col-span-4 hidden lg:flex justify-center">
            <img
              src={LOGO_URL}
              alt="Nada's Kids Garden"
              className="h-64 w-auto object-contain"
            />
          </div>
        </div>

        <div className="mt-12 flex flex-wrap gap-2 relative">
          {categories.map((c) => {
            const active = filter === c;
            return (
              <button
                key={c}
                data-testid={`shop-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`rounded-full px-5 py-2.5 text-sm font-medium border-2 transition-all ${
                  active
                    ? "bg-brand-sun text-brand-ink border-brand-sun shadow-sunny"
                    : "bg-white border-brand-line text-brand-ink hover:border-brand-sun"
                }`}
              >
                {c}
              </button>
            );
          })}
        </div>
      </section>

      <section className="section-pad pt-0">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="bg-white rounded-[28px] p-6 border border-brand-line h-[460px] animate-pulse"
              >
                <div className="bg-brand-beige h-56 rounded-2xl" />
                <div className="bg-brand-beige h-4 rounded mt-6 w-3/4" />
                <div className="bg-brand-beige h-3 rounded mt-3 w-1/2" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {shown.map((p, i) => {
              const Icon = categoryIcon[p.category] || BookOpen;
              return (
                <Reveal key={p.id} delay={(i % 3) * 0.08}>
                  <div
                    data-testid={`product-card-${p.id}`}
                    className="group bg-white rounded-[28px] border-2 border-brand-line overflow-hidden h-full flex flex-col hover:-translate-y-1 hover:shadow-sunny hover:border-brand-sun/50 transition-all duration-500"
                  >
                    <div className="relative aspect-[4/5] overflow-hidden bg-gradient-to-br from-brand-honey/30 to-brand-leafSoft/15 grid place-items-center p-6">
                      <img
                        src={p.image}
                        alt={`${p.title} book cover`}
                        className="max-h-full max-w-full object-contain drop-shadow-[0_12px_30px_rgba(44,36,22,0.18)] transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                      <div className="absolute top-4 left-4 bg-brand-cream/95 backdrop-blur-md text-xs uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border-2 border-brand-sun/40 inline-flex items-center gap-1.5 text-brand-leafDeep font-medium">
                        <Icon size={12} /> {p.category}
                      </div>
                      {p.is_new && (
                        <div
                          data-testid={`product-new-badge-${p.id}`}
                          className="absolute top-4 right-4 bg-brand-rust text-brand-cream text-[11px] uppercase tracking-[0.22em] font-bold px-3 py-1.5 rounded-full shadow-earthy flex items-center gap-1.5 animate-bob"
                        >
                          <Sparkles size={12} /> New
                        </div>
                      )}
                      {(() => {
                        const pricing = getPricing(p.price);
                        return pricing.hasDiscount ? (
                          <div
                            data-testid={`product-discount-badge-${p.id}`}
                            className={`absolute ${p.is_new ? "top-16" : "top-4"} right-4 bg-brand-leafDeep text-brand-honey text-[11px] uppercase tracking-[0.22em] font-bold px-3 py-1.5 rounded-full shadow-earthy`}
                          >
                            −{pricing.percentOff}% OFF
                          </div>
                        ) : null;
                      })()}
                    </div>
                    <div className="p-7 flex-1 flex flex-col">
                      <h3 className="font-heading text-xl mb-1">{p.title}</h3>
                      {p.subtitle && (
                        <p className="text-sm text-brand-muted italic mb-2 font-body">
                          {p.subtitle}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2 mb-4 mt-1">
                        {p.pages && (
                          <span className="text-[11px] uppercase tracking-[0.18em] text-brand-leafDeep bg-brand-honey/40 rounded-full px-3 py-1 font-medium">
                            {p.pages} pages
                          </span>
                        )}
                        {p.age_group && (
                          <span className="text-[11px] uppercase tracking-[0.18em] text-brand-leafDeep bg-brand-leafSoft/30 rounded-full px-3 py-1 font-medium">
                            {p.age_group}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-brand-muted leading-relaxed font-body flex-1">
                        {p.problem}
                      </p>
                      <div className="mt-6 flex items-end justify-between gap-3">
                        {(() => {
                          const pricing = getPricing(p.price);
                          const currency = (p.currency || "usd").toUpperCase();
                          return pricing.hasDiscount ? (
                            <div
                              data-testid={`product-price-${p.id}`}
                              className="flex flex-col"
                            >
                              <div className="flex items-baseline gap-2">
                                <span className="font-brand text-2xl text-brand-sunDeep">
                                  {formatUSD(pricing.sale)}
                                </span>
                                <span className="text-sm text-brand-muted line-through font-body">
                                  {formatUSD(pricing.original)}
                                </span>
                                <span className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-body">
                                  {currency}
                                </span>
                              </div>
                              <span
                                data-testid={`product-savings-${p.id}`}
                                className="text-[11px] font-medium text-brand-leafDeep mt-1 font-body"
                              >
                                You save {formatUSD(pricing.savings)} · {pricing.percentOff}% off
                              </span>
                            </div>
                          ) : (
                            <span className="font-brand text-2xl text-brand-sunDeep">
                              {formatUSD(p.price)} <span className="text-sm text-brand-muted">({currency})</span>
                            </span>
                          );
                        })()}
                        <Link
                          data-testid={`product-view-${p.id}`}
                          to={`/shop/${p.id}`}
                          className="rounded-full bg-brand-sun hover:bg-brand-sunDeep text-brand-ink px-5 py-2.5 text-xs font-medium inline-flex items-center gap-2 transition-colors shrink-0"
                        >
                          View <ArrowRight size={14} />
                        </Link>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
